import type { ApiClient } from '@carbonwebmail/api-client'
import type { ProfileDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * The account of the person using the product — the part of it this product
 * owns, which is the name.
 *
 * The password, the address and the second factor are the identity provider's
 * and are edited in its own embedded screen, which keeps its own state. So
 * there is one form here and one "saved" flag, and no risk of one form's
 * confirmation appearing over the other's.
 */

export type ProfileState = AsyncState<ProfileDto>

export function useProfile({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.profile(), [client])
  const { state } = useLoaded<ProfileDto>(
    load,
    'We could not load your profile. Please try again in a moment.',
  )

  const [name, setName] = useState<string | undefined>(undefined)
  const [nameSaved, setNameSaved] = useState(false)
  const [nameProblem, setNameProblem] = useState<string | undefined>(undefined)
  const [savingName, setSavingName] = useState(false)

  /**
   * Filled once, when the profile arrives.
   *
   * Guarded on the field rather than on the loaded value, so a reload cannot
   * overwrite what somebody has typed since.
   */
  useEffect(() => {
    if (state.status !== 'ready' || name !== undefined) return
    setName(state.value.name)
  }, [state, name])

  const changeName = useCallback((value: string) => {
    setName(value)
    setNameSaved(false)
    setNameProblem(undefined)
  }, [])

  const saveName = useCallback(async () => {
    if (name === undefined) return
    setSavingName(true)
    setNameProblem(undefined)
    try {
      await client.saveProfileName(name)
      setNameSaved(true)
    } catch {
      setNameProblem('We could not save your name. Please try again in a moment.')
    } finally {
      setSavingName(false)
    }
  }, [client, name])

  return {
    state,
    name,
    nameSaved,
    nameProblem,
    savingName,
    changeName,
    saveName,
  }
}
